import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Anchor, RefreshCw, Search, MessageSquare, FileText, Bot, CheckCircle2, AlertTriangle, Phone } from 'lucide-react';
import HelpTip from '@/components/HelpTip';
import ChatbotTreeEditor from '@/components/chatbot/ChatbotTreeEditor';

// F1 — panel del número ancla, 2 ejes por tenant (contrato con la sesión de flows):
//  · Eje VENDEDOR  -> flow_tenants.modo      ∈ {flow|plantilla|chatbot}  ("Aviso al vendedor")
//  · Eje LEAD      -> flow_tenants.modo_lead ∈ {ia|flow|chatbot}         ("Atención al lead", default ia)
// El eje lead es aditivo e INERTE por ahora (su motor se conecta en la siguiente fase).
const MODO_META = {
  flow: { label: 'Formulario (Flow)', icon: MessageSquare },
  plantilla: { label: 'Aviso de texto', icon: FileText },
  chatbot: { label: 'Chatbot', icon: Bot },
};
const MODO_LEAD_META = {
  ia: { label: 'Atención por IA' },
  flow: { label: 'Formulario (Flow)' },
  chatbot: { label: 'Chatbot' },
};
const selectCls = 'w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-sm text-white';

export default function NumeroAncla() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [editChatbot, setEditChatbot] = useState(null);

  const load = async () => {
    setData(null);
    try {
      const r = await api.get('/admin/platform/anchor/tenants');
      setData(r.data || { tenants: [] });
    } catch (e) {
      setData({ tenants: [] });
      toast.error(e.response?.data?.detail || 'No se pudo cargar el panel del número ancla');
    }
  };
  useEffect(() => { load(); }, []);

  const tenants = data?.tenants || [];
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return tenants;
    return tenants.filter((t) => (t.name || '').toLowerCase().includes(s) || (t.company_id || '').includes(s));
  }, [tenants, q]);

  // Eje VENDEDOR (escribe flow_tenants.modo) — con candados: texto exige plantilla aprobada; chatbot bloqueado.
  const setModo = async (t, modo) => {
    if (!modo || modo === t.modo_efectivo) return;
    if (modo === 'plantilla' && !t.texto_aprobado) {
      toast.error('Ese tenant no tiene plantilla de texto aprobada; no se puede poner en "Aviso de texto" sin dejarlo sin aviso.');
      return;
    }
    if (modo === 'chatbot') {
      toast.error('El modo Chatbot aún no está disponible (motor en construcción).');
      return;
    }
    if (!window.confirm(`Vas a cambiar el aviso al vendedor de "${t.name}" a: ${MODO_META[modo]?.label}. Es inmediato y reversible. ¿Continuar?`)) return;
    setBusy(t.company_id);
    try {
      const r = await api.post(`/admin/platform/anchor/tenants/${t.company_id}/modo`, { modo });
      const antes = MODO_META[r.data?.modo_anterior]?.label || r.data?.modo_anterior;
      toast.success(`"${t.name}": aviso al vendedor = ${MODO_META[modo]?.label} (antes: ${antes}).`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar el aviso al vendedor');
    } finally {
      setBusy('');
    }
  };

  // Eje LEAD (escribe flow_tenants.modo_lead) — aditivo, inerte por ahora (el motor se conecta después).
  const setModoLead = async (t, modo_lead) => {
    const current = t.modo_lead || 'ia';
    if (!modo_lead || modo_lead === current) return;
    if (!window.confirm(`Vas a fijar la atención al lead de "${t.name}" en: ${MODO_LEAD_META[modo_lead]?.label}. Cambio inmediato y reversible. ¿Continuar?`)) return;
    setBusy(t.company_id);
    try {
      const r = await api.post(`/admin/platform/anchor/tenants/${t.company_id}/modo_lead`, { modo_lead });
      const antes = MODO_LEAD_META[r.data?.modo_lead_anterior]?.label || r.data?.modo_lead_anterior;
      toast.success(`"${t.name}": atención al lead = ${MODO_LEAD_META[modo_lead]?.label} (antes: ${antes}).`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar la atención al lead');
    } finally {
      setBusy('');
    }
  };

  // Eje LEAD (mezcla): la IA puede adjuntar botones/listas cuando convenga. Bandera flow_tenants.ia_interactiva.
  const setIaInteractiva = async (t, val) => {
    setBusy(t.company_id);
    try {
      await api.post(`/admin/platform/anchor/tenants/${t.company_id}/ia_interactiva`, { ia_interactiva: val });
      toast.success(`"${t.name}": botones/listas de la IA ${val ? 'ACTIVADOS' : 'desactivados'}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar la IA interactiva');
    } finally {
      setBusy('');
    }
  };

  if (editChatbot) return <ChatbotTreeEditor companyId={editChatbot.company_id} companyName={editChatbot.name} onBack={() => setEditChatbot(null)} />;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Anchor className="w-5 h-5" /> Número Ancla — cómo llega el lead
            <HelpTip text="Por cada tenant que usa el número compartido de la plataforma, define dos cosas: cómo se ATIENDE al lead (por IA, Formulario o Chatbot) y cómo se le AVISA al vendedor (Formulario o texto). Cambios inmediatos y reversibles; nunca deja a un tenant sin aviso." />
          </CardTitle>
          <CardDescription>
            Por tenant: cómo se atiende al lead y cómo se avisa al vendedor. Dos ejes independientes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
              <Input className="pl-8" placeholder="Buscar tenant…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Button variant="outline" size="sm" onClick={load} title="Recargar"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {data === null && <div className="text-sm text-white/60 px-1">Cargando…</div>}
      {data !== null && shown.length === 0 && <div className="text-sm text-white/60 px-1">No hay tenants en el número ancla.</div>}

      <div className="grid grid-cols-1 gap-3">
        {shown.map((t) => {
          const eff = MODO_META[t.modo_efectivo] || {};
          const EffIcon = eff.icon || MessageSquare;
          const leadCur = t.modo_lead || 'ia';
          return (
            <Card key={t.company_id}>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-xs text-white/40 truncate">{t.company_id}</div>
                  </div>
                  <Badge variant={t.migrado ? 'default' : 'secondary'} className="shrink-0">
                    {t.migrado ? 'En el ancla' : 'No migrado'}
                  </Badge>
                </div>

                <div className="rounded-md border border-white/10 p-2.5 space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-white/80 font-medium">Atención al lead</span>
                    <HelpTip text="Cómo se ATIENDE al lead entrante (por el WhatsApp del propio tenant): por IA (como hoy) o por Chatbot (arma su árbol con «Configurar chatbot»). Cambio inmediato y reversible." />
                  </div>
                  <select
                    className={selectCls}
                    value={leadCur}
                    disabled={busy === t.company_id}
                    onChange={(e) => setModoLead(t, e.target.value)}
                  >
                    <option value="ia">Atención por IA</option>
                    <option value="flow">Formulario (Flow)</option>
                    <option value="chatbot">Chatbot</option>
                  </select>
                  {(t.modo_lead === 'chatbot' || t.has_tree) && (
                    <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => setEditChatbot(t)}>
                      Configurar chatbot
                    </Button>
                  )}
                  {leadCur === 'ia' && (
                    <label className="mt-2 flex items-start gap-2 text-xs text-white/70 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0"
                        checked={!!t.ia_interactiva}
                        disabled={busy === t.company_id}
                        onChange={(e) => setIaInteractiva(t, e.target.checked)}
                      />
                      <span>
                        <span className="text-white/90 font-medium">Botones y listas inteligentes</span>
                        <span className="text-white/50"> — la IA decide cuándo mandar botones o listas junto a su respuesta (no la reemplaza). Reversible.</span>
                      </span>
                    </label>
                  )}
                </div>

                <div className="rounded-md border border-white/10 p-2.5 space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-white/80 font-medium">Aviso al vendedor</span>
                    <HelpTip text="Cómo se le AVISA al vendedor del nuevo lead: Formulario (Flow) interactivo o Aviso de texto. Nunca deja a un tenant sin aviso (no permite texto sin plantilla aprobada)." />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/60">
                    <EffIcon className="w-3.5 h-3.5" />
                    <span>Hoy: <span className="text-white/90">{eff.label || t.modo_efectivo}</span></span>
                    {t.modo_set == null && <span className="text-white/40">(por defecto)</span>}
                  </div>
                  <select
                    className={selectCls}
                    value={t.modo_efectivo}
                    disabled={busy === t.company_id}
                    onChange={(e) => setModo(t, e.target.value)}
                  >
                    <option value="flow">Formulario (Flow)</option>
                    <option value="plantilla" disabled={!t.texto_aprobado}>
                      {t.texto_aprobado ? 'Aviso de texto' : 'Aviso de texto — sin plantilla aprobada'}
                    </option>
                    <option value="chatbot" disabled>Chatbot — próximamente</option>
                  </select>
                  <div className="flex items-center gap-1 text-[11px]">
                    {t.texto_aprobado
                      ? <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 className="w-3 h-3" /> Puede recibir por texto</span>
                      : <span className="flex items-center gap-1 text-amber-400"><AlertTriangle className="w-3 h-3" /> Sin plantilla — modo texto no disponible</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <CanalVozPanel />
    </div>
  );
}


// F1 — Canal de voz por tenant (aditivo, NO toca el panel ancla de arriba).
// Lee/escribe companies.voz_canal via /admin/platform/voz/tenants. WhatsApp (WABA) principal,
// Twilio respaldo. Fail-open: si la salida por WhatsApp no esta disponible, el efectivo es twilio.
const CANAL_META = {
  twilio: { label: 'Twilio (teléfono)' },
  whatsapp: { label: 'WhatsApp (WABA)' },
  auto: { label: 'Automático' },
};

function CanalVozPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');

  const load = async () => {
    setData(null);
    try {
      const r = await api.get('/admin/platform/voz/tenants');
      setData(r.data || { tenants: [] });
    } catch (e) {
      setData({ tenants: [] });
      toast.error(e.response?.data?.detail || 'No se pudo cargar el canal de voz');
    }
  };
  useEffect(() => { load(); }, []);

  const tenants = data?.tenants || [];
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return tenants;
    return tenants.filter((t) => (t.name || '').toLowerCase().includes(s) || (t.company_id || '').includes(s));
  }, [tenants, q]);
  const disponible = !!data?.whatsapp_calling_disponible;

  const setCanal = async (t, canal) => {
    if (!canal || canal === t.canal_preferido) return;
    if (!window.confirm(`Vas a fijar el canal de voz de "${t.name}" en: ${CANAL_META[canal]?.label}. Inmediato y reversible. ¿Continuar?`)) return;
    setBusy(t.company_id);
    try {
      const r = await api.post(`/admin/platform/voz/tenants/${t.company_id}/canal`, { canal });
      const ef = CANAL_META[r.data?.canal_efectivo]?.label || r.data?.canal_efectivo;
      toast.success(`"${t.name}": canal preferido = ${CANAL_META[canal]?.label} (efectivo hoy: ${ef}).`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar el canal de voz');
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" /> Canal de voz por tenant
            <HelpTip text="Por dónde SALEN y ENTRAN las llamadas de cada tenant: WhatsApp (WABA del propio tenant) como principal o Twilio como respaldo. Si la salida por WhatsApp aún no está disponible, cae a Twilio automáticamente — nadie se queda sin llamar." />
          </CardTitle>
          <CardDescription>
            {disponible
              ? 'WhatsApp (WABA) principal, Twilio respaldo. La salida por WhatsApp está disponible.'
              : 'WhatsApp (WABA) principal, Twilio respaldo. La SALIDA por WhatsApp aún no está disponible globalmente: el canal efectivo es Twilio aunque elijas WhatsApp. La ENTRADA por WhatsApp ya opera.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
              <Input className="pl-8" placeholder="Buscar tenant…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Button variant="outline" size="sm" onClick={load} title="Recargar"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {data === null && <div className="text-sm text-white/60 px-1">Cargando…</div>}
      {data !== null && shown.length === 0 && <div className="text-sm text-white/60 px-1">No hay empresas.</div>}

      <div className="grid grid-cols-1 gap-3">
        {shown.map((t) => {
          const pref = t.canal_preferido || 'twilio';
          const efect = t.canal_efectivo || 'twilio';
          const mismatch = pref !== efect;
          return (
            <Card key={t.company_id}>
              <CardContent className="pt-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-xs text-white/40 truncate">{t.company_id}</div>
                  </div>
                  <Badge variant={efect === 'whatsapp' ? 'default' : 'secondary'} className="shrink-0">
                    Hoy: {CANAL_META[efect]?.label || efect}
                  </Badge>
                </div>
                <select
                  className={selectCls}
                  value={pref}
                  disabled={busy === t.company_id}
                  onChange={(e) => setCanal(t, e.target.value)}
                >
                  <option value="twilio">Twilio (teléfono) — respaldo</option>
                  <option value="whatsapp">WhatsApp (WABA) — principal</option>
                  <option value="auto">Automático — WhatsApp si se puede, si no Twilio</option>
                </select>
                {mismatch && (
                  <div className="flex items-start gap-1 text-[11px] text-amber-400">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>Elegiste {CANAL_META[pref]?.label}, pero hoy sale por {CANAL_META[efect]?.label} (salida WhatsApp no disponible aún).</span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
}
